// Notification event taxonomy.
//
// One canonical list of events that can fire a notification, shared by daemon
// and client. The settings UI reads `NOTIFICATION_EVENTS` to render toggles;
// the daemon reads it to decide which entries to skip when sending push.
//
// To add a new event: add it here, then emit `NotificationEventId` from the
// place that triggers it. Old clients without the toggle default to the
// `defaultEnabled` value below — keep new events on by default unless a
// large fraction of users won't want them.

import { z } from "zod";

export type NotificationEventId = "agent_finished" | "agent_error" | "agent_permission";

export interface NotificationEventDefinition {
  id: NotificationEventId;
  /** Short label shown next to the toggle in Settings. */
  label: string;
  /** One-line description shown under the label. */
  description: string;
  /** Whether the toggle is on for a freshly-installed client. */
  defaultEnabled: boolean;
}

export const NOTIFICATION_EVENTS: readonly NotificationEventDefinition[] = [
  {
    id: "agent_finished",
    label: "Agent finished",
    description: "When an agent completes its task.",
    defaultEnabled: true,
  },
  {
    id: "agent_permission",
    label: "Permission requested",
    description: "When an agent is waiting for you to approve a tool, plan, or question.",
    defaultEnabled: true,
  },
  {
    id: "agent_error",
    label: "Agent error",
    description: "When an agent encounters an error and stops.",
    defaultEnabled: false,
  },
] as const;

export const NotificationEventIdSchema = z.enum([
  "agent_finished",
  "agent_error",
  "agent_permission",
]);

/**
 * Per-device, per-host preferences. Stored per-event, so adding new events
 * later doesn't invalidate existing prefs — undefined entries fall back to
 * `defaultEnabled` from the registry.
 */
export const NotificationPreferencesSchema = z.object({
  /**
   * Master switch. When false, no event fires regardless of per-event toggles.
   * Lets the user "mute this device" without losing their per-event choices.
   */
  enabled: z.boolean().optional(),
  events: z.record(NotificationEventIdSchema, z.boolean()).optional(),
});

export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

/** Map an `AgentAttentionReason` to the event id used by the registry. */
export function reasonToEventId(reason: "finished" | "error" | "permission"): NotificationEventId {
  switch (reason) {
    case "finished":
      return "agent_finished";
    case "error":
      return "agent_error";
    case "permission":
      return "agent_permission";
  }
}

/**
 * Decide whether an entry with `prefs` should receive a notification for
 * `eventId`. Missing prefs (legacy registrations) fall back to the registry
 * default. Master switch off short-circuits everything.
 */
export function shouldDeliverEvent(
  prefs: NotificationPreferences | null | undefined,
  eventId: NotificationEventId,
): boolean {
  if (prefs?.enabled === false) return false;
  const explicit = prefs?.events?.[eventId];
  if (typeof explicit === "boolean") return explicit;
  const def = NOTIFICATION_EVENTS.find((e) => e.id === eventId);
  return def?.defaultEnabled ?? true;
}

/** Returns prefs filled with the registry defaults, leaving overrides intact. */
export function withDefaults(prefs: NotificationPreferences | null | undefined): Required<
  Pick<NotificationPreferences, "enabled">
> & {
  events: Record<NotificationEventId, boolean>;
} {
  const events: Record<NotificationEventId, boolean> = {
    agent_finished: true,
    agent_error: false,
    agent_permission: true,
  };
  for (const def of NOTIFICATION_EVENTS) {
    events[def.id] = def.defaultEnabled;
  }
  if (prefs?.events) {
    for (const [k, v] of Object.entries(prefs.events)) {
      if (typeof v === "boolean") {
        events[k as NotificationEventId] = v;
      }
    }
  }
  return {
    enabled: prefs?.enabled ?? true,
    events,
  };
}
